/**
 * Class to highlight texts in DOM elements
 * This service is based on css of background's properties, and it won't be applied to any elements which have background-color property.
 */

import { HighlightHelper } from "@/Common/Helpers/HighlightHelper";

class HighlightService {
    public allElementsToHighlight: DOMRect[];
    public searchText: string;
    public currentScrollTop: number;
    public currentScrollLeft: number;
    private currentScrollWidth: number;
    private currentScrollHeight: number;
    public index: number;
    private headElement: HTMLHeadElement;
    private highlightingStyleElement: HTMLStyleElement;
    private styleSelector: string;
    private parentContainer: HTMLElement;
    private scrollContainer: HTMLElement;
    private mutationObserver: MutationObserver;
    private getAllNodesToHighlightFunc: (searchText: string) => DOMRect[];
    private highlightEvent: () => void;
    private indexedBackgroundColor: string;
    private defaultBackgroundColor: string;

    /**
     * Create a service for highlighting.
     * @param {HTMLElement} container - The container to search nodes
     * @param {HTMLElement} scrollContainer - The container that has scroll
     * @param {string} styleSelectorName
     * @param {string} searchText
     * @param {(searchText: string) => DOMRect[]} getAllNodesToHighlightFunc - The function is provided by a component to highlight texts in DOM elements.
     */
    constructor(container: HTMLElement, scrollContainer: HTMLElement, styleSelectorName: string, searchText: string, getAllNodesToHighlightFunc: (searchText: string) => DOMRect[] = null) {
        this.headElement = document.getElementsByTagName("head")[0];
        this.highlightingStyleElement = document.createElement("style");
        this.highlightingStyleElement.id = styleSelectorName;
        this.parentContainer = container;
        this.scrollContainer = scrollContainer;
        this.styleSelector = `.${styleSelectorName}`;
        this.searchText = searchText;
        this.allElementsToHighlight = [];
        this.indexedBackgroundColor = "linear-gradient(#F28F19, #F28F19)";
        this.defaultBackgroundColor = "linear-gradient(rgba(0, 133, 255, 0.4), rgba(0, 133, 255, 0.4))";
        this.index = -1;
        this.currentScrollTop = 0;
        this.currentScrollLeft = 0;
        this.currentScrollWidth = 0;
        this.currentScrollHeight = 0;
        this.mutationObserver = new MutationObserver(() => {
            this.execute(this.searchText, false);
        });
        this.getAllNodesToHighlightFunc = getAllNodesToHighlightFunc ?? ((searchText: string) => this.defaultTraverseFunc(this.parentContainer, searchText, false, HighlightHelper.invalidWidth));
        this.highlightEvent = () => {
            if (this.isScrollContainerSynced()) {
                this.onHighlightText();
            }
            else {
                this.execute(this.searchText, false);
            }
        };
    }

    // public
    /**
     * Initialize the service.
     * Add a style element to the <head></head>. Add resize and scroll events. Set resize observer.
     */
    public initialize(): void {
        this.addStyleToHead();

        window.addEventListener("resize", this.highlightEvent, { passive: true });
        window.addEventListener("scroll", this.highlightEvent, { passive: true });

        if (this.parentContainer) {
            this.mutationObserver.observe(this.parentContainer, { attributes: false, childList: true, subtree: true });
        }

        if (this.scrollContainer) {
            this.scrollContainer.addEventListener("scroll", this.highlightEvent, { passive: true });
        }

        this.index = 0;
    }

    /**
     * Terminate the service.
     * Remove the style element from <head></head>. Unset resize observer.
     */
    public terminate(): void {
        this.removeStyleFromHead();

        window.removeEventListener("resize", this.highlightEvent);
        window.removeEventListener("scroll", this.highlightEvent);

        if (this.scrollContainer) {
            this.scrollContainer.removeEventListener("scroll", this.highlightEvent);
        }

        this.index = -1;
        this.mutationObserver.disconnect();
    }

    /**
     * Execute the service.
     * @param {string} searchText
     * @param {boolean} isIndexReset
     */
    public execute(searchText: string, isIndexReset: boolean): void {
        if (this.scrollContainer && !IsNullOrUndefined(searchText)) {
            this.updateSearchText(searchText);
            this.searchNodeToHighlight();

            this.currentScrollWidth = this.scrollContainer.scrollWidth;
            this.currentScrollHeight = this.scrollContainer.scrollHeight;
            this.currentScrollTop = this.scrollContainer.scrollTop;
            this.currentScrollLeft = this.scrollContainer.scrollLeft;
        }

        if (isIndexReset) {
            this.index = 0;
        }

        this.onHighlightText();
    }

    // private
    /**
     * Find all text nodes to highlight and apply highlighting to found nodes.
     */
    private onHighlightText(): void {
        if (this.allElementsToHighlight.length > 0) {
            this.highlightingStyleElement.innerHTML = this.buildCssClass(this.allElementsToHighlight);
        }
        else {
            this.highlightingStyleElement.innerHTML = "";
        }
    }

    /**
     * Search all elements to highlight and set the initial position of the current scroll.
     */
    private searchNodeToHighlight(): void {
        this.allElementsToHighlight = this.getAllNodesToHighlightFunc(this.searchText)?.sortByProperties(propertyComparer(p => p.y), propertyComparer(p => p.x)) ?? [];
    }

    /**
     * Update the search text and reset the index
     * @param {string} newSearchText
     */
    private updateSearchText(newSearchText: string): void {
        this.searchText = newSearchText ?? "";
    }

    /**
     * Add style to <head></head>
     */
    private addStyleToHead(): void {
        if (this.highlightingStyleElement && this.headElement) {
            this.headElement.append(this.highlightingStyleElement);
        }
    }

    /**
     * Remove style from <head></head>
     */
    private removeStyleFromHead(): void {
        if (this.highlightingStyleElement) {
            this.highlightingStyleElement.remove();
        }
    }

    /**
     * Build style.
     * @param {DOMRect[]} textRects
     * @returns {string}
     */
    private buildCssClass(textRects: DOMRect[]): string {
        const parentRect: DOMRect = this?.parentContainer?.getClientRects?.()?.[0] ?? null;

        if (IsNullOrWhiteSpace(this.styleSelector) || !parentRect) {
            return "";
        }
        else {
            let backgroundImage: string = "background-image: ";
            let backgroundSize: string = "background-size: ";
            let backgroundPosition: string = "background-position: ";
            let hasElementToHighlight: boolean = false;

            for (let i = 0; i < textRects?.length; i++) {
                if (textRects[i] && this.isElementVisibleInCurrentScrollY(textRects[i], parentRect)) {
                    if (i === this.index) {
                        backgroundImage = backgroundImage + this.indexedBackgroundColor + ",";
                    }
                    else {
                        backgroundImage = backgroundImage + this.defaultBackgroundColor + ",";
                    }

                    backgroundSize = backgroundSize + this.getBackgroundSize(textRects[i]) + ",";
                    backgroundPosition = backgroundPosition + this.getBackgroundPosition(textRects[i], parentRect) + ",";
                    hasElementToHighlight = true;
                }
            }

            return hasElementToHighlight ? `${this.styleSelector} {
                background-repeat: no-repeat;
                ${this.updateCommaToSemicolonAtTheEnd(backgroundImage)}
                ${this.updateCommaToSemicolonAtTheEnd(backgroundSize)}
                ${this.updateCommaToSemicolonAtTheEnd(backgroundPosition)}
            }` : "";
        }
    }

    /**
     * Return background size.
     * @param {DOMRect} textRect - A node's position and size.
     * @returns {string}
     */
    private getBackgroundSize(textRect: DOMRect): string {
        return `${textRect.width}px ${textRect.height}px`;
    }

    /**
     * Return background position.
     * @param {DOMRect} textRect - A node's position and size.
     * @param {DOMRect} parentRect - The parent container that contains the node.
     * @returns {string}
     */
    private getBackgroundPosition(textRect: DOMRect, parentRect: DOMRect): string {
        return `${textRect.x - parentRect.x - this.scrollContainer.scrollLeft + this.currentScrollLeft}px ${textRect.y - parentRect.y - this.scrollContainer.scrollTop + this.currentScrollTop}px`;
    }

    /**
     * Replace the last comma with a semicolon in a string
     * @param {string} styleText - a string to update.
     * @returns {string}
     */
    private updateCommaToSemicolonAtTheEnd(styleText: string): string {
        if (!IsNullOrUndefined(styleText) && styleText[styleText.length - 1] === ",") {
            return styleText.slice(0, -1) + ";";
        }
        else {
            return styleText;
        }
    }

    /**
     * Return boolean if an element is visible in the parent's container.
     * @param {DOMRect} elementRect - An element's DOMRect to check its visibility.
     * @param {DOMRect} parentRect - An element's DOMRect that contains elementRect
     * @returns {boolean}
     */
    private isElementVisibleInCurrentScrollY(elementRect: DOMRect, parentRect: DOMRect): boolean {
        if (!elementRect || !parentRect) {
            return false;
        }
        else {
            const parentScrollStartY = parentRect.y;
            const parentScrollEndY = (this.parentContainer?.clientHeight ?? 0) + parentRect.y;
            const currentElementY = elementRect.y - this.scrollContainer.scrollTop + this.currentScrollTop;

            return parentScrollStartY <= currentElementY && currentElementY <= parentScrollEndY;
        }
    }

    /**
     * A default traverse function
     * @param {ChildNode} node - a node to traverse to search a text
     * @param {string} searchText
     * @param {boolean} isOverflowing
     * @param {number} width
     * @returns {DOMRect[]}
     */
    private defaultTraverseFunc(node: ChildNode, searchText: string, isOverflowing: boolean, width: number): DOMRect[] {
        const domRects: DOMRect[] = [];

        if (node && !IsNullOrWhiteSpace(searchText)) {
            const nodeElement = node as Element;
            const htmlElement = node as HTMLElement;
            const htmlElementStyle = !htmlElement ? null : getComputedStyle(htmlElement);

            isOverflowing = isOverflowing || htmlElementStyle?.overflow === "hidden";
            width = HighlightHelper.getWidth(htmlElement, isOverflowing, width);

            if (HighlightHelper.isBackgroundColorTransparent(htmlElementStyle?.backgroundColor)) {
                for (let i = 0; i < node?.childNodes?.length; i++) {
                    if (node.childNodes[i].nodeName === "#text") {
                        const textNode = node.childNodes[i] as Text;

                        if (textNode?.length > 0) {
                            domRects.push(...HighlightHelper.getTextRectsToHighlightWithChildNode(nodeElement, textNode, searchText, width));
                        }
                    }
                    else {
                        domRects.push(...this.defaultTraverseFunc(node.childNodes[i], searchText, isOverflowing, width));
                    }
                }
            }
        }

        return domRects;
    }

    /**
     * Return if the saved scrollContainer's scroll width and height is equal to scrollContainer's scroll width and height
     * @returns {boolean}
     */
    private isScrollContainerSynced(): boolean {
        return Math.floor(this.scrollContainer.scrollWidth) === Math.floor(this.currentScrollWidth) &&
            Math.floor(this.scrollContainer.scrollHeight) === Math.floor(this.currentScrollHeight);
    }
}

export { HighlightService };
